import { useState, useEffect, useMemo, useCallback, useImperativeHandle, RefObject } from "react";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import cn from "classnames";
import { Alert, AlertType } from "@components/new/Alert";
import TextInput from "@components/TextInput/TextInput";
import DataTable from '@containers/DataTableNew';
import NumberInput from "@components/new/NumberInput";
import { Marketplace } from "@components/new/Marketplace";
import Photo from "@components/new/Photo";
import { H4 } from "@components/new/Typo";
import { formatNumber } from "@utils/formatValues";
// import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
// import ItemSearchItem from "../../components/ItemSearchItem";
import { TableCellLink, TableColumn } from "@components/new/Table";
import { MessageModalContent } from '@components/modals/MessageModalNew';
import { Tooltip } from '@components/new/Tooltip';
import {
  createFBPWithdrawalRequest,
  getFBPStocks,
  isWbRequestItem,
  isYmRequestItem,
  isAliRequestItem,
  isUnknownRequestItem,
  TFBPStockItem,
  TYmItem,
  TWbItem,
  TAliItem,
  TRequestParams,
} from "@api/apiClient/fbp";
import { FormHandle } from "./types";
import emptyImg from '@assets/img/empty.png';
import SearchIcon from "@assets/icons/outline/search.svg?react";
import NoItemsIcon from "@assets/icons/hugeicons/package-remove.svg?react";
import StoreIcon from "@assets/icons/outline/store.svg?react";
import styles from './Form.module.scss';

type TWbItemWithCount = TWbItem & { count?: number }
type TYmItemWithCount = TYmItem & { count?: number }
type TAliItemWithCount = TAliItem & { count?: number }
const MAX_ITEMS = 100;

export interface FormType {
  wbId: string;
  wbSellerId: string;
  ymSellerId: string;
}

type TRow = (TYmItem | TWbItem | TAliItem) & {
  count?: number,
  present: number,
  selected: boolean,
  marketplace: string,
};

const Form = ({ formRef, onStatusChange, onSuccess, onError, sellerWarehouseId }: {
  formRef?: RefObject<FormHandle>;
  onStatusChange: (props: {
    disabled: boolean,
    isLoading?: boolean,
  }, msg?: React.ReactNode) => void;
  onSuccess: () => void;
  onError: () => void;
  sellerWarehouseId: string;
  // fbpWarehouseId: string;
}) => {
  const currentLanguage = i18n.language;
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState<TRow[]>([]);
  const [searchString, setSearchString] = useState<string>("");
  //const searchStringLC = searchString.toLowerCase();
  /*const itemsFiltered = items.filter(v => (
    v.name.toLowerCase().includes(searchStringLC) ||
    (isWbRequestItem(v) && String(v.wbNmId).toLowerCase().includes(searchStringLC)) ||
    (isYmRequestItem(v) && String(v.sku).toLowerCase().includes(searchStringLC))
  ));*/

  const getIsValid = () => {
    return (selectedItems?.length > 0) &&
      (selectedItems?.length <= 100) &&
      !selectedItems.find(v => (!v.count));
  }

  const stocksGetter = useCallback(
    ({ filters, sorting, page }: TRequestParams) => {
      return getFBPStocks({ filters, sorting, page, sellerWarehouseId }).then((data) => ({
        ...data,
        items: data.items.reduce((acc: TRow[], item: TFBPStockItem) => {
          return [
            ...acc,
            ...item.ymItems.map(v => ({
              ...v.ymProduct,
              present: Math.min(v.present ?? 0, 99999999),
              count: 0,
              selected: false, // !item.matchingLinkName,
              matchingLinkName: item.matchingLinkName,
            })),
            ...item.wbItems.map(v => ({
              ...v.wbProduct,
              present: v.present,
              count: 0,
              selected: false, // !item.matchingLinkName,
              matchingLinkName: item.matchingLinkName,
            })),
            ...item.aliItems.map(v => ({
              ...v.aliProduct,
              present: Math.min(v.present ?? 0, 99999999),
              count: 0,
              selected: false, // !item.matchingLinkName,
              matchingLinkName: item.matchingLinkName,
            })),
          ];
        }, [])
      }));
    },
    [sellerWarehouseId]
  );

  const handleSelectionChange = useCallback((
    selectedIds: string[],
    items: TRow[],
  ) => {
    setSelectedItems((oldSelected: TRow[]) => {
      return (
        selectedIds.reduce((acc: TRow[], s: string) => {
          const item = oldSelected.find(v => v.id === s) || items.find(v => v.id === s);
          if (item) return [...acc, item];
          return acc;
        }, [])
      );
    });
  }, [selectedItems, setSelectedItems]);

  const {
    isLoading,
    mutate,
  } = useMutation({
    mutationFn: createFBPWithdrawalRequest,
    onSuccess: () => onSuccess(),
    onError: () => onError(),
  });

  useEffect(() => {
    onStatusChange({
      disabled: !getIsValid(),
      isLoading: Boolean(isLoading),
    }, selectedItems.find(v => (v.selected && !v.count)) ? (
      <Alert
        type={AlertType.ERROR}
        title={t("fbpPage.withdrawalModal.emptyCountAlert")}
      />
    ) : undefined);
  }, [isLoading, selectedItems])

  const onSubmit = () => {
    mutate({
      sellerWarehouseId,
      ymProducts: (selectedItems.filter(v => (isYmRequestItem(v))) as TYmItemWithCount[]).map(v => ({
        ymProductId: v.id,
        count: v.count ?? 0,
      })),
      wbProducts: (selectedItems.filter(v => (isWbRequestItem(v))) as TWbItemWithCount[]).map(v => ({
        wbProductId: v.id,
        count: v.count ?? 0,
      })),
      aliProducts: (selectedItems.filter(v => (isAliRequestItem(v))) as TAliItemWithCount[]).map(v => ({
        aliProductId: v.id,
        count: v.count ?? 0,
      })),
    });
  };

  useImperativeHandle(formRef, () => ({
    submit: onSubmit,
  }));

  const handleUpdateCount = useCallback((v: number, id: string) => {
    setSelectedItems((oldItems) => {
      const newItems = [...oldItems];
      const idx = newItems.findIndex(v => v.id === id);
      if (idx > -1) newItems[idx].count = v;
      return newItems;
    })
  }, [selectedItems, setSelectedItems])

  const columns: TableColumn<TRow>[] = useMemo(() => [
    {
      header: t("fbpPage.createOrder.items.photo"),
      renderCell: (row) => (
        isUnknownRequestItem(row.data) ? (
          <img src={emptyImg} alt="" className={styles.emptyImage} />
        ) : (
          <Photo src={row.data.primaryImage} />
        )
      ),
      width: "80px",
      fixWidth: true,
    },
    {
      header: t("fbpPage.createOrder.items.name"),
      renderCell: (row) => {
        const description = isWbRequestItem(row.data) ? row.data.wbNmId : (
          isAliRequestItem(row.data) ? row.data.skuCode : row.data.sku
        );
        return (
          <div className={styles.mpCell}>
            <Marketplace name={row.data.marketplace} size="small" />
            <TableCellLink
              title={row.data.name}
              description={description}
              thirdLine={row.data.sellerName ? (
                <span className={styles.storeLine}>
                  <StoreIcon /> {row.data.sellerName}
                </span>
              ) : undefined}
              to={row.data.url}
              external
              maxWidth="460px"
            />
          </div>
        )
      },
      width: "200px",
    },
    {
      header: t("fbpPage.createOrder.items.size"),
      valueGetter: (row) => ((isWbRequestItem(row) && row.wbTechSize) || ""),
      width: "50px",
      fixWidth: true,
      alignment: "center",
    },
    {
      header: t("fbpPage.createOrder.items.count"),
      renderCell: (row) => {
        const selected = selectedItems.find(v => v.id === row.data.id);
        const present = row.data.present;
        return (
          <NumberInput
            value={selected?.count || 0}
            onChange={(v) => handleUpdateCount(v, row.data.id)}
            min={0}
            max={Math.min(present, 99999999)}
            suffix={present > 99999999 ? `/${formatNumber(99999999)}+` : `/${formatNumber(present)}`}
            wrapSuffix={present > 9999}
            disabled={!selected}
            isWide
            hasError={selected && !selected.count}
          />
        );
      },
      width: "180px",
      fixWidth: true,
    },
  ], [currentLanguage, selectedItems, handleUpdateCount]);

  return (
    <div className={styles.form}>
      <div className={cn(styles.section, styles.sectionItems)}>
        <H4>{t('fbpPage.withdrawalModal.selectItems')}</H4>

        <div className={styles.inputRow}>
          <Tooltip
            action={
              <TextInput
                value={searchString}
                onChange={setSearchString}
                placeholder={t('fbpPage.withdrawalModal.searchInput')}
                prefix={<SearchIcon />}
                className={styles.search}
              />
            }
            position="top"
            text={t('fbpPage.withdrawalModal.searchToltip')}
          />
        </div>

        <DataTable<TRow>
          selectable
          isCheckboxDisabled={(data: TRow) => (selectedItems.length >= MAX_ITEMS && !selectedItems.find(v => data.id === v.id))}
          checkboxTooltip={(data: TRow) => ((selectedItems.length >= MAX_ITEMS && !selectedItems.find(v => data.id === v.id)) ? t("fbpPage.delStoreModal.checkboxMaxCountTooltip") : "")}
          selectedIds={selectedItems.map(v => v.id)}
          columns={columns}
          defaultPageSize={MAX_ITEMS}
          filters={[{
            name: "skus",
            value: searchString,
          }]}
          queryKey={["getFBPStocks", sellerWarehouseId]}
          onSelectionChange={handleSelectionChange}
          getData={stocksGetter}
          verticalAlignment="center"
          noLoaderOnRefresh
          pending={!sellerWarehouseId}
          hidePaginationDropdown
          maxSelected={MAX_ITEMS}
          resetOnUpdate={false}
          keepSearchParams={false}
          keepSelectionOnFiltersChange
          pageSizeOptions={[MAX_ITEMS]}
          notFound={() => (
            <MessageModalContent
              title={t('fbpPage.withdrawalModal.noItemsTitle')}
              description={t('fbpPage.withdrawalModal.noItemsDescription')}
              variant="info"
              Icon={<NoItemsIcon />}
              iconSize="l"
            />
          )}
        />
      </div>
    </div>
  )
};

export default Form;

//key={JSON.stringify(items.map(v => v.id))}
//rowKey={(v: TRow) => `${v.id}-${v.selected}`}
